import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { Paper, Stack, Typography } from '@mui/material';
import { DashWidget } from '@/components/dashboards/widgets/widget-insight/Widget';
import { DashSourceProfile, Label, WidgetTypeCode } from '@/graphql/generated/types.generated';
import { memo, ReactNode, useMemo } from 'react';
import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import {
    AutoGraphRounded,
    BarChartRounded,
    Looks6Rounded,
    PieChartRounded,
    SsidChartRounded,
    TableChartRounded,
} from '@mui/icons-material';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';

type ActionResponse = {
    widgets: {
        widget: DashWidget;
        filters: {
            profiles: DashSourceProfile[];
            dates: { startDate: Date; endDate: Date };
            labels: Label[];
            datesCompare?: { startDate: Date; endDate: Date };
        };
    }[];
};

export type AndieLoadWidgetDataProps = ActionRenderAi<ActionResponse>;

const AndieLoadWidgetData = memo((props: AndieLoadWidgetDataProps) => {
    const { t, i18n } = useTranslation();

    const widgetTypeIcon: Partial<Record<WidgetTypeCode, ReactNode>> = useMemo(() => {
        return {
            [WidgetTypeCode.BigNumber]: <Looks6Rounded />,
            [WidgetTypeCode.Table]: <TableChartRounded />,
            [WidgetTypeCode.Bars]: <BarChartRounded />,
            [WidgetTypeCode.Lines]: <SsidChartRounded />,
            [WidgetTypeCode.Pie]: <PieChartRounded />,
            [WidgetTypeCode.BarsHorizontal]: <BarChartRounded />,
        };
    }, []);

    return (
        <AndieGenericToolRender<ActionResponse>
            title={() => t('andie.tool.widget-data.title')}
            icon={<AutoGraphRounded fontSize="small" />}
            loadingMessages={() => [t('andie.tool.widget-data.loading')]}
            completeMessage={({ result }) =>
                t('andie.tool.widget-data.complete', {
                    value: result && Array.isArray(result) ? result.length : 0,
                })
            }
            additionalComponent={(props) => {
                const isComplete = props.status === ActionResponseStatusAi.COMPLETE;

                if (!isComplete) {
                    return null;
                }

                return props.result && Array.isArray(props.result) ? (
                    <Stack>
                        {props.result.map(({ widget }, index) => {
                            const normalizedCode = widget.type!.code.replace(/-/g, '_').toUpperCase() as WidgetTypeCode;
                            return (
                                <Stack key={index}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1,
                                            borderTop: index > 0 ? 1 : 0,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            {widgetTypeIcon[normalizedCode]}
                                            <Typography variant="body2" fontWeight={500}>
                                                {i18n.exists(widget.name) ? t(widget.name) : widget.name}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Stack>
                            );
                        })}
                    </Stack>
                ) : null;
            }}
            {...props}
        />
    );
});

export default AndieLoadWidgetData;
